import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@services/redux";
import { PhoneForm } from "@components/molecules/phone-form";
import { PATTERNS } from "@constants/patterns.constants";
import { updateDraftOrder } from "../../services/redux/order/order.reducer";
import { useGroupOrder } from "../../services/group-order/group-order.context";
import {
  budgetLabel,
  missingLabel,
  toPhoneDisplay,
} from "../../services/group-order/group-order.utils";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export const FinishOrderDialog = ({ open, onClose }: { open: boolean; onClose(): void }) => {
  const ctx = useGroupOrder();
  const dispatch = useAppDispatch();
  const draft = useAppSelector((s) => s.order.draft);
  const [name, setName] = useState(draft.contactPerson || "");
  // Same phone field as checkout: formats as (555) 555-1234 while typing.
  const { control, watch, reset } = useForm({
    mode: "onChange",
    defaultValues: { contactPhone: toPhoneDisplay(draft.contactNo) },
  });
  const phone = watch("contactPhone") || "";
  const [instruction, setInstruction] = useState(draft.instruction || "");
  const [sending, setSending] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const [sendError, setSendError] = useState(false);

  // The dialog stays mounted, so reload the fields from the cart each time it opens
  // (it may have changed since: a refresh, "Make changes", another tab).
  useEffect(() => {
    if (!open) return;
    setName(draft.contactPerson || "");
    setInstruction(draft.instruction || "");
    reset({ contactPhone: toPhoneDisplay(draft.contactNo) });
    // Only on open: later draft changes come from this dialog's own fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!ctx || ctx.mode !== "office") return null;
  const rep = ctx.groupOrder?.repFirstName || "your rep";
  const phoneValid = !!phone && PATTERNS.phone.test(phone);
  const valid = name.trim().length > 0 && phoneValid;

  const send = async () => {
    setSending(true);
    setMissing([]);
    setSendError(false);
    // finish() reads the draft straight from the store, so this update is included in its save.
    dispatch(
      updateDraftOrder({ contactPerson: name.trim(), contactNo: phone.trim(), instruction }),
    );
    const res = await ctx.finish();
    setSending(false);
    if (res.ok) return onClose();
    const notReady = "missing" in res && res.missing ? res.missing : [];
    setMissing(notReady);
    // No missing list: the save or the send failed (a network failure shows no snackbar).
    if (!notReady.length) setSendError(true);
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send this order to {rep}?</DialogTitle>
      <DialogContent>
        <p>
          {budgetLabel(ctx.budget, (n: number) => money.format(n))}. {rep} will get a text and an
          email to review and pay.
        </p>
        {missing.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Before sending, please {missing.map(missingLabel).join(", ")}.
          </Alert>
        )}
        {sendError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            We couldn't send this yet. Please try again.
          </Alert>
        )}
        {ctx.conflictNotice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {ctx.conflictNotice}
          </Alert>
        )}
        <h6 style={{ marginTop: 16 }}>On-site contact</h6>
        <p style={{ fontSize: 14 }}>The caterer calls this person on delivery day.</p>
        <TextField
          fullWidth
          required
          margin="dense"
          label="Contact name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 100 }}
        />
        <PhoneForm
          id="groupContactPhone"
          label="Contact phone"
          variant="outlined"
          fullWidth
          required
          margin="dense"
          type="tel"
          control={control}
          name="contactPhone"
          rules={{ required: "Phone number is required" }}
        />
        <TextField
          fullWidth
          margin="dense"
          multiline
          minRows={2}
          label="Delivery instructions (optional)"
          placeholder="Suite, front desk, parking…"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Keep editing
        </Button>
        <Button variant="contained" onClick={send} disabled={!valid || sending}>
          {sending ? "Sending…" : `Send to ${rep}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
